// src/pages/firm/user/FirmUserDialog.tsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Password } from "primereact/password";
import { InputSwitch } from "primereact/inputswitch";
import { InputNumber } from "primereact/inputnumber";
import { toast } from "react-toastify";
import { Plus, Save, X, User as UserIcon, ShieldCheck, Activity, Landmark, Search, UserCheck, CircleUser, Wallet, Warehouse, Printer as PrinterIcon, Loader2, CheckCircle, XCircle } from "lucide-react";

// Types & Stores
import { User } from "../../../types/user";
import { Customer } from "../../../types/customer";
import { Firm } from "../../../types/firm";
import { getTurkishRoleName, RoleType } from "../../../types/role";
import { useRoleStore } from "../../../store/roleStore";
import { useEmployeeStore } from "../../../store/employeeStore";
import { useCashStore } from "../../../store/cashStore";
import { useBankStore } from "../../../store/bankStore";

import { IconTextButton } from "../../../components/ButtonComponents";
import api from "../../../service/api";
import { ETabs } from "../../../components/ETabs";
import { useSelectDialog } from "../../../hooks/useSelectDialog";
import { CustomSelectInput } from "../../../components/CustomSelectInput";
import { AccountPrintSettings } from "../../AccountPrintSettings";
import { Employee } from "../../../types/employee";
import { Cash } from "../../../types/cash";
import { Bank } from "../../../types/bank";
import { useWarehouseStore } from "../../../store/warehouse";
import { useCustomerStore } from "../../../store/customerStore";

export type UserFormData = Omit<User, "id" | "firm" | "account" | "role"> & {
  role?: any; // RoleType yerine any veya Partial<RoleType> kullanarak katı kuralı esnetiyoruz
  firm?: string;
  account: string | null;
  isActive?: boolean;
  pinCode?: string | null;
  staffDetail?: {
    canLiftHeavy: boolean;
    hasForklift: boolean;
    isPicker: boolean;
    isPacker: boolean;
    isPreparer: boolean;
    canEnterWeight?: boolean;
    canUploadPhoto?: boolean;
    canManageSecondUnit?: boolean;
    experienceLevel: number;
    speedMultiplier: number;
    onlySeeAssigned: boolean;
    staffSettings?: string;
  };
  integrationDetail?: {
    employeeId: number | null;
    defaultWarehouseCode: string;
    orderSeries: string;
    invoiceSeries: string;
    collectionSeries: string;
    allowedCashIds: number[];
    allowedBankIds: number[];
    defaultCashId: number | null;
    defaultBankId: number | null;
    customEarnRate: number | null;
    customMaxDiscountRate: number | null;
  };
};

const initialFormData: UserFormData = {
  firmId: 0,
  firm: "",
  accountId: null,
  account: null,
  roleId: 0,
  role: { id: 0, name: "", level: 0, roleGroupId: 0, roleGroup: "", users: "" },
  name: "",
  email: "",
  password: "",
  isActive: true,
  pinCode: null,
  staffDetail: {
    canLiftHeavy: false,
    hasForklift: false,
    isPicker: false,
    isPreparer: false,
    isPacker: false,
    canEnterWeight: false,
    canUploadPhoto: false,
    canManageSecondUnit: false,
    onlySeeAssigned: false,
    experienceLevel: 1,
    speedMultiplier: 1.0,
    staffSettings: ""
  },
  integrationDetail: {
    employeeId: null,
    defaultWarehouseCode: "",
    orderSeries: "",
    invoiceSeries: "",
    collectionSeries: "",
    allowedCashIds: [],
    allowedBankIds: [],
    defaultCashId: null,
    defaultBankId: null,
    customEarnRate: null,
    customMaxDiscountRate: null
  }
};

interface UserDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | null;
  onSave: (userData: any) => void;
  currentFirmId: number;
  currentFirmName: string;
}

export const FirmUserDialog: React.FC<UserDialogProps> = ({
  isOpen, onClose, user, onSave, currentFirmId, currentFirmName,
}) => {
  const [formData, setFormData] = useState<UserFormData>(initialFormData);
  const [activeIndex, setActiveIndex] = useState(0);
  const [roleOptions, setRoleOptions] = useState<any[]>([]);
  const [isPinLoading, setIsPinLoading] = useState(false);
  const [isPinValid, setIsPinValid] = useState<boolean | null>(null);
  const latestPinRef = useRef<string>("");

  const [displayNames, setDisplayNames] = useState({
    employeeName: "",
    cashNames: "",
    bankNames: "", warehouseName: ""
  });

  const roleStore = useRoleStore();
  const { fetchDataDynamic: fetchEmployees, loading: empLoading, data: employees } = useEmployeeStore();
  const { fetchDataDynamic: fetchCashes, loading: cashLoading, data: cashes } = useCashStore();
  const { fetchDataDynamic: fetchBanks, loading: bankLoading, data: banks } = useBankStore();
  const { fetchDataDynamic: fetchWarehouses, loading: warehouseLoading, data: warehouse } = useWarehouseStore();
  const { fetchDataDynamic: fetchAccounts, loading: accountLoading, data: accounts } = useCustomerStore();

  const accountSelectDialog = useSelectDialog<Customer>();
  const employeeSelect = useSelectDialog<Employee>();
  const cashSelect = useSelectDialog<Cash>();
  const bankSelect = useSelectDialog<Bank>();
  const warehouseSelect = useSelectDialog<any>();

  useEffect(() => {
    if (!isOpen) return;
    setActiveIndex(0);
    roleStore.fetchDataDynamic().then((res: any) => {
      const formatted = (res?.items || []).map((r: any) => ({
        id: r.id,
        displayName: getTurkishRoleName(r.name),
        name: r.name,
        level: r.level,
        roleGroup: r.roleGroup?.name || "",
        users: String(r._count?.users || "0")
      }));
      setRoleOptions(formatted);
    });

    fetchEmployees();
    fetchCashes();
    fetchBanks();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    if (user) {
      const settings = (user as any).settings || {};
      const allowedCashes = (user as any).allowedCashes || [];
      const allowedBanks = (user as any).allowedBanks || [];
      const staff = (user as any).staffDetail || initialFormData.staffDetail;

      setFormData({
        firmId: user.firmId || currentFirmId,
        firm: (user.firm as Firm)?.name || currentFirmName,
        accountId: user.accountId,
        account: (user.account as any)?.name || null,
        roleId: user.roleId,
        role: user.role,
        name: user.name,
        email: user.email,
        password: "",
        isActive: user.isActive ?? false,
        pinCode: (user as any).pinCode || null,
        staffDetail: {
          ...staff,
          staffSettings: staff.staffSettings || ""
        },
        integrationDetail: {
          employeeId: (user as any).employee?.id || null,
          defaultWarehouseCode: settings.warehouseCode || "",
          orderSeries: settings.orderSeries || "",
          invoiceSeries: "", // API'de karşılığı varsa ekle
          collectionSeries: settings.paymentSeries || "",
          allowedCashIds: allowedCashes.map((c: any) => c.cashId),
          allowedBankIds: allowedBanks.map((b: any) => b.bankId),
          defaultCashId: settings.defaultCashId,
          defaultBankId: settings.defaultBankId,
          customEarnRate: settings.customEarnRate || null,
          customMaxDiscountRate: settings.customMaxDiscountRate || null
        }
      });

      // DisplayNames'i doğrudan user içindeki alt objelerden alıyoruz
      setDisplayNames({
        employeeName: (user as any).employee ? `${(user as any).employee.firstName} ${(user as any).employee.lastName}` : "",
        // API'den gelen orijinal nesnelerdeki isimleri kullan:
        cashNames: allowedCashes.map((c: any) => c.cash?.name).filter(Boolean).join(", "),
        bankNames: allowedBanks.map((b: any) => b.bank?.name).filter(Boolean).join(", "),
        warehouseName: settings.warehouseCode || ""
      });
    } else {
      // Yeni kayıt modu
      setFormData({ ...initialFormData, firmId: currentFirmId, firm: currentFirmName });
      setDisplayNames({ employeeName: "", cashNames: "", bankNames: "", warehouseName: "" });
    }
  }, [user, isOpen]); // Sadece user değişiminde veya dialog açıldığında çalışır

  const handleStaffChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, staffDetail: { ...prev.staffDetail!, [field]: value } }));
  };

  const handleIntegrationChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      integrationDetail: {
        ...prev.integrationDetail!,
        [field]: value
      }
    }));
  };



  const handleGeneratePin = async () => {
    const toastId = toast.loading("PIN kodu üretiliyor...");
    try {
      setIsPinLoading(true);
      const response = await api.get("/user/generate-pin");
      if (response.data && response.data.data) {
        setFormData(p => ({ ...p, pinCode: response.data.data }));
        setIsPinValid(true);
        toast.update(toastId, { render: "Yeni PIN kodu oluşturuldu.", type: "success", isLoading: false, autoClose: 2000 });
      }
    } catch (error) {
      toast.update(toastId, { render: "PIN kodu oluşturulurken bir hata oluştu.", type: "error", isLoading: false, autoClose: 3000 });
    } finally {
      setIsPinLoading(false);
    }
  };

  const checkPin = async (pin: string) => {
    if (pin.length !== 4) return;
    latestPinRef.current = pin;
    const toastId = toast.loading("PIN kodu kontrol ediliyor...");
    try {
      setIsPinValid(null);
      const userId = user?.id ? `&userId=${user.id}` : "";
      const response = await api.get(`/user/check-pin?pin=${pin}${userId}`);
      
      if (latestPinRef.current !== pin) {
        toast.dismiss(toastId);
        return;
      }

      if (response.data && response.data.isUsed) {
        toast.update(toastId, { render: response.data.message || "Bu PIN kodu kullanılmaktadır!", type: "error", isLoading: false, autoClose: 3000 });
        setFormData(p => ({ ...p, pinCode: null }));
        setIsPinValid(false);
      } else {
        toast.update(toastId, { render: "PIN kodu kullanılabilir.", type: "success", isLoading: false, autoClose: 2000 });
        setIsPinValid(true);
      }
    } catch (error) {
      if (latestPinRef.current !== pin) {
        toast.dismiss(toastId);
        return;
      }
      console.error("PIN kontrol hatası:", error);
      toast.update(toastId, { render: "PIN kontrolünde hata oluştu.", type: "error", isLoading: false, autoClose: 3000 });
      setIsPinValid(false);
    }
  };

  const handlePinBlur = () => {
    if (formData.pinCode && formData.pinCode.length > 0 && formData.pinCode.length < 4) {
      toast.error("PIN kodu 4 haneli olmalıdır!");
      setFormData(p => ({ ...p, pinCode: null }));
    }
  };

  const openEmployeeSelect = async () => {
    const res = await fetchEmployees({ pageIndex: 1, pageSize: 10 });
    const selected = await employeeSelect.open({
      title: "Personel Seç",
      data: res?.items || [],
      // ARAMA ALANLARINI BURADA DÜZELTİYORUZ
      searchFields: [
        { field: "firstName", operator: "contains" },
        { field: "lastName", operator: "contains" }
      ],
      columns: [
        { field: "firstName", header: "Ad" },
        { field: "lastName", header: "Soyad" }
      ],
      onFilter: async (p) => {
        // p.filter artık iç içe geçmiş firstName ve lastName filtrelerini içerir
        const r = await fetchEmployees({ pageIndex: p.pageIndex, pageSize: p.pageSize, filter: p.filter });
        return { data: r.items, total: r.total };
      }
    });
    if (selected) {
      handleIntegrationChange('employeeId', selected.id);
      setDisplayNames(p => ({ ...p, employeeName: `${selected.firstName} ${selected.lastName}` }));
    }
  };

  const openCashSelect = async () => {
    const res = await fetchCashes({ pageIndex: 1, pageSize: 10 });
    const selecteds = await cashSelect.open({
      title: "Yetkili Kasaları Seç",
      data: res?.items || [],
      multiple: true,
      initialSelection: cashes.filter(c => formData.integrationDetail?.allowedCashIds.includes(c.id)),
      columns: [{ field: "name", header: "Kasa Adı" }],
      onFilter: async (p) => {
        const r = await fetchCashes({ pageIndex: p.pageIndex, pageSize: p.pageSize, filter: p.filter });
        return { data: r.items, total: r.total };
      }
    });
    if (!selecteds) return;
    if (selecteds) {
      const selectedIds = selecteds.map((s: any) => s.id);
      const firstId = selectedIds.length > 0 ? selectedIds[0] : null;

      setFormData(prev => ({
        ...prev,
        integrationDetail: {
          ...prev.integrationDetail!,
          allowedCashIds: selectedIds,
          defaultCashId:
            prev.integrationDetail!.defaultCashId &&
              selectedIds.includes(prev.integrationDetail!.defaultCashId)
              ? prev.integrationDetail!.defaultCashId
              : firstId
        }
      }));

      setDisplayNames(p => ({ ...p, cashNames: selecteds.map((s: any) => s.name).join(", ") }));
    }
  };

  const openBankSelect = async () => {
    const res = await fetchBanks({ pageIndex: 1, pageSize: 10 });
    const selecteds = await bankSelect.open({
      title: "Yetkili Bankaları Seç",
      data: res?.items || [],
      multiple: true, loading: bankLoading,
      initialSelection: banks.filter(b => formData.integrationDetail?.allowedBankIds.includes(b.id)),
      columns: [{ field: "externalId", header: "Banka Adı" }, { field: "name", header: "Banka Adı" }],
      onFilter: async (p) => {
        const r = await fetchBanks({ pageIndex: p.pageIndex, pageSize: p.pageSize, filter: p.filter });
        return { data: r.items, total: r.total };
      }
    });

    if (selecteds) {
      const selectedIds = selecteds.map((s: any) => s.id);
      const firstId = selectedIds.length > 0 ? selectedIds[0] : null;

      setFormData(prev => ({
        ...prev,
        integrationDetail: {
          ...prev.integrationDetail!,
          allowedBankIds: selectedIds,
          defaultBankId:
            prev.integrationDetail!.defaultBankId &&
              selectedIds.includes(prev.integrationDetail!.defaultBankId)
              ? prev.integrationDetail!.defaultBankId
              : firstId
        }
      }));

      setDisplayNames(p => ({ ...p, bankNames: selecteds.map((s: any) => s.name).join(", ") }));
    }
  };

  //102 036
  const openWarehouseSelect = async () => {
    // 1. Depoları Store'dan çek (WarehouseStore kullandığını varsayıyorum)
    const res = await fetchWarehouses({ pageIndex: 1, pageSize: 10 });

    // 2. Dialog'u aç
    const selected = await warehouseSelect.open({
      title: "Varsayılan Depo Seç",
      data: res?.items || [],
      multiple: false, // Genelde 1 kullanıcı için 1 varsayılan depo olur
      loading: warehouseLoading,
      columns: [
        { field: "name", header: "Depo Adı" }
      ],
      onFilter: async (p) => {
        // Filtreleme yaparken yine Warehouse fetch fonksiyonunu kullanmalısın
        const r = await fetchWarehouses({
          pageIndex: p.pageIndex,
          pageSize: p.pageSize,
          filter: p.filter
        });
        return { data: r.items, total: r.total };
      }
    });

    if (selected) {
      // Hem id hem de code için string dönüşümü yapalım
      const codeValue = selected.code ? String(selected.code) : String(selected.id);
      handleIntegrationChange('defaultWarehouseCode', codeValue);
      setDisplayNames(p => ({ ...p, warehouseName: selected.name }));
    }
  };

  const openAccountSelect = async () => {
    // İlk veriyi çek (Page 1)
    const res = await fetchAccounts({ pageIndex: 1, pageSize: 10 });

    const selected = await accountSelectDialog.open({
      title: "Cari (Hesap) Seç",
      data: res?.items || [],
      loading: accountLoading,
      columns: [
        { field: "name", header: "Cari Ünvan" },
        { field: "taxNumber", header: "VKN/TCKN" }
      ],
      onFilter: async (p) => {
        // Arama yapıldığında store'u kullan
        const r = await fetchAccounts({
          pageIndex: p.pageIndex,
          pageSize: p.pageSize,
          filter: p.filter
        });
        return { data: r.items, total: r.total };
      }
    });

    if (selected) {
      setFormData(prev => ({
        ...prev,
        accountId: selected.id,
        account: selected.name // Input içinde görünen isim
      }));
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    if (e && e.preventDefault) e.preventDefault();

    const payload: any = {
      name: formData.name,
      email: formData.email,
      roleId: formData.roleId,
      firmId: formData.firmId,
      accountId: formData.accountId,
      isActive: formData.isActive,
      pinCode: formData.pinCode,
      ...(formData.password ? { password: formData.password } : {}),
      staffDetail: formData.staffDetail,
    };

    const integration = formData.integrationDetail;
    if (integration) {
      payload.employeeId = integration.employeeId;
      payload.allowedCashIds = integration.allowedCashIds;
      payload.allowedBankIds = integration.allowedBankIds;

      payload.settings = {
        orderSeries: integration.orderSeries,
        invoiceSeries: integration.invoiceSeries, // Varsa ekle demiştin
        paymentSeries: integration.collectionSeries,
        warehouseCode: integration.defaultWarehouseCode ? String(integration.defaultWarehouseCode) : "",
        defaultCashId: integration.defaultCashId,
        defaultBankId: integration.defaultBankId,
        customEarnRate: integration.customEarnRate,
        customMaxDiscountRate: integration.customMaxDiscountRate,
      };
    }

    onSave(payload);
    onClose();
  };
  const renderStaffTab = () => (
    <div className="space-y-6 p-2">
      {/* Yetenekler ve Fiziksel Özellikler Grid */}
      <h3 className="text-sm font-bold text-text mb-2 border-b border-borderColor pb-1">Görev ve Yetkinlikler</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {[
          { id: 'isPicker', label: 'Toplayıcı', icon: '📦' },
          { id: 'isPacker', label: 'Paketleyici', icon: '🎁' },
          { id: 'isPreparer', label: 'Hazırlayıcı / Takviye', icon: '🔄' },
          { id: 'hasForklift', label: 'Forklift Operatörü', icon: '🚜' },
          { id: 'canLiftHeavy', label: 'Ağır Yük Kaldırabilir', icon: '🏋️‍♂️' },
        ].map((item) => (
          <div key={item.id} className={`flex items-center justify-between p-3 border rounded-xl transition-all ${formData.staffDetail?.[item.id as keyof typeof formData.staffDetail] ? 'card2 shadow-sm border-blue-200' : 'card'}`}>
            <div className="flex items-center gap-3">
              <span className="text-xl">{item.icon}</span>
              <label className="text-sm font-medium text-text">{item.label}</label>
            </div>
            <InputSwitch checked={!!formData.staffDetail?.[item.id as keyof typeof formData.staffDetail]} onChange={(e) => handleStaffChange(item.id, e.value)} />
          </div>
        ))}
      </div>

      <h3 className="text-sm font-bold text-text mb-2 border-b border-borderColor pb-1">Veri Girişi & İşlem İzinleri</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {[
          { id: 'canEnterWeight', label: 'Ağırlık/Gramaj Girebilir', icon: '⚖️' },
          { id: 'canUploadPhoto', label: 'Fotoğraf Yükleyebilir', icon: '📷' },
          { id: 'canManageSecondUnit', label: '2. Birim Girebilir', icon: '📦' },
        ].map((item) => (
          <div key={item.id} className={`flex items-center justify-between p-3 border rounded-xl transition-all ${formData.staffDetail?.[item.id as keyof typeof formData.staffDetail] ? 'card2 shadow-sm border-blue-200' : 'card'}`}>
            <div className="flex items-center gap-3">
              <span className="text-xl">{item.icon}</span>
              <label className="text-sm font-medium text-text">{item.label}</label>
            </div>
            <InputSwitch checked={!!formData.staffDetail?.[item.id as keyof typeof formData.staffDetail]} onChange={(e) => handleStaffChange(item.id, e.value)} />
          </div>
        ))}
      </div>

      {/* 🌟 Operasyonel Kısıtlamalar (Yeni Bölüm) */}
      <div className="card border-l-4 border-l-orange-500 p-4 bg-orange-50/10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center">
              <UserCheck size={20} />
            </div>
            <div>
              <h4 className="text-sm font-black text-text uppercase tracking-tight">Sadece Atanan İşleri Görsün</h4>
              <p className="text-[11px] font-medium text-text opacity-60">Aktif edilirse personel iş havuzunu göremez, sadece amiri tarafından atanan işleri yapar.</p>
            </div>
          </div>
          <InputSwitch
            checked={!!formData.staffDetail?.onlySeeAssigned}
            onChange={(e) => handleStaffChange('onlySeeAssigned', e.value)}
          />
        </div>
      </div>

      {/* 🌟 İş Akışı Modu (JSON olarak staffSettings içinde tutulur) */}
      <div className="card p-4 rounded-xl border mt-4">
        <h4 className="font-bold text-primary mb-4 flex items-center gap-2">
          <Activity size={18} /> İş Akışı Atama Tercihleri
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* Toplama İş Akışı */}
          <div className="flex flex-col gap-2">
            <label className="text-[12px] font-black opacity-60 uppercase tracking-widest text-text">
              Toplama (Picking) Modu
            </label>
            <Dropdown
              value={(() => {
                try {
                  const settings = JSON.parse(formData.staffDetail?.staffSettings || "{}");
                  return settings.pickerTaskMode || 'select';
                } catch (e) {
                  return 'select';
                }
              })()}
              options={[
                { label: 'Sıradakini Seç (Listeden)', value: 'select' },
                { label: 'Otomatik Başla (Sıradaki gelsin)', value: 'auto' }
              ]}
              onChange={(e) => {
                let currentSettings = {};
                try {
                  currentSettings = JSON.parse(formData.staffDetail?.staffSettings || "{}");
                } catch (err) { }
                const newSettings = { ...currentSettings, pickerTaskMode: e.value };
                handleStaffChange('staffSettings', JSON.stringify(newSettings));
              }}
              className="w-full"
            />
            <small className="text-[10px] text-gray-500">Toplama işlerinde personelin sıradaki işi nasıl alacağını belirler.</small>
          </div>

          {/* Paketleme İş Akışı */}
          <div className="flex flex-col gap-2">
            <label className="text-[12px] font-black opacity-60 uppercase tracking-widest text-text">
              Paketleme (Packing) Modu
            </label>
            <Dropdown
              value={(() => {
                try {
                  const settings = JSON.parse(formData.staffDetail?.staffSettings || "{}");
                  return settings.packerTaskMode || 'select';
                } catch (e) {
                  return 'select';
                }
              })()}
              options={[
                { label: 'Sıradakini Seç (Listeden)', value: 'select' },
                { label: 'Otomatik Başla (Sıradaki gelsin)', value: 'auto' }
              ]}
              onChange={(e) => {
                let currentSettings = {};
                try {
                  currentSettings = JSON.parse(formData.staffDetail?.staffSettings || "{}");
                } catch (err) { }
                const newSettings = { ...currentSettings, packerTaskMode: e.value };
                handleStaffChange('staffSettings', JSON.stringify(newSettings));
              }}
              className="w-full"
            />
            <small className="text-[10px] text-gray-500">Paketleme işlerinde personelin sıradaki işi nasıl alacağını belirler.</small>
          </div>

        </div>
      </div>

      {/* Performans Parametreleri Kısmı */}
      <div className="card2 p-4 rounded-xl border border-borderColor/30 bg-bgAlt/20 mt-4">
        <h4 className="font-bold text-text mb-4 flex items-center gap-2">
          <Activity size={18} className="text-primary" /> Performans Parametreleri
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col gap-2">
            <label className="text-[12px] font-black opacity-60 uppercase tracking-widest">Tecrübe Seviyesi (1-5)</label>
            <InputNumber
              value={formData.staffDetail?.experienceLevel}
              onValueChange={(e) => handleStaffChange('experienceLevel', e.value)}
              min={1}
              max={5}
              showButtons
              className="w-full"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-[12px] font-black opacity-60 uppercase tracking-widest">Hız Çarpanı (Örn: 1.2)</label>
            <InputNumber
              value={formData.staffDetail?.speedMultiplier}
              onValueChange={(e) => handleStaffChange('speedMultiplier', e.value)}
              min={0.1}
              max={3}
              step={0.1}
              showButtons
              mode="decimal"
              minFractionDigits={1}
              className="w-full"
            />
          </div>
        </div>
      </div>
    </div>
  );



  const renderIntegrationTab = () => (
    <div className="space-y-6 p-2">
      <div className="card2 p-4 rounded-xl border border-borderColor bg-bgAlt">
        <h4 className="font-bold text-primary mb-4 flex items-center gap-2"><UserIcon size={18} /> Mikro Personel & Depo</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <CustomSelectInput
              label="Bağlı Personel"
              value={displayNames.employeeName || ""}
              placeholder="Seçiniz..."
              icon={UserIcon}
              onClick={openEmployeeSelect}
            />
          </div>
          <div className="flex flex-col gap-2">
            <CustomSelectInput
              label="Varsayılan Depo"
              value={displayNames.warehouseName || ""}
              placeholder="Depo Seçiniz..."
              icon={Warehouse}
              onClick={openWarehouseSelect}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-orange-700">Sipariş Seri</label>
          <InputText maxLength={10} value={formData.integrationDetail?.orderSeries} onChange={(e) => handleIntegrationChange('orderSeries', e.target.value.toUpperCase())} />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-green-700">Fatura Seri</label>
          <InputText maxLength={10} value={formData.integrationDetail?.invoiceSeries} onChange={(e) => handleIntegrationChange('invoiceSeries', e.target.value.toUpperCase())} />
        </div>
        <div className="flex flex-col gap-2">
          <label className="text-sm font-semibold text-primary">Tahsilat Seri</label>
          <InputText maxLength={10} value={formData.integrationDetail?.collectionSeries} onChange={(e) => handleIntegrationChange('collectionSeries', e.target.value.toUpperCase())} />
        </div>
      </div>

      <div className="card p-4 border border-borderColor rounded-xl bg-bgAlt">
        <h4 className="font-bold mb-4 flex items-center gap-2 text-primary"><Landmark size={18} /> Finansal Yetkiler</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-b pb-4 mb-4">
          <div className="flex flex-col gap-2">
            <CustomSelectInput
              label="Yetkili Kasalar"
              value={displayNames.cashNames || ""}
              placeholder="Seçiniz..."
              icon={Wallet}
              onClick={openCashSelect}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold">Varsayılan Kasa</label>
            <Dropdown
              value={formData.integrationDetail?.defaultCashId}
              options={cashes.filter(c => formData.integrationDetail?.allowedCashIds.includes(c.id))}
              optionLabel="name" optionValue="id" placeholder="Seçiniz"
              onChange={(e) => handleIntegrationChange('defaultCashId', e.value)}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col gap-2">
            <CustomSelectInput
              label="Yetkili Bankalar"
              value={displayNames.bankNames || ""}
              placeholder="Seçiniz..."
              icon={Landmark}
              onClick={openBankSelect}
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold">Varsayılan Banka</label>
            <Dropdown
              value={formData.integrationDetail?.defaultBankId}
              options={banks.filter(b => formData.integrationDetail?.allowedBankIds.includes(b.id))}
              optionLabel="name" optionValue="id" placeholder="Seçiniz"
              onChange={(e) => handleIntegrationChange('defaultBankId', e.value)}
            />
          </div>
        </div>
      </div>

      {/* Plasiyer İskonto Havuzu - Özel Oranlar */}
      <div className="card p-4 border rounded-xl bg-orange-50/30">
        <h4 className="font-bold mb-4 flex items-center gap-2 text-orange-700">
          <Landmark size={18} /> İskonto Havuzu (Kullanıcıya Özel)
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold">Özel Kazanım Oranı (%)</label>
            <InputText
              type="number"
              step="0.1"
              value={formData.integrationDetail?.customEarnRate?.toString() || ""}
              onChange={(e) => handleIntegrationChange('customEarnRate', e.target.value ? parseFloat(e.target.value) : null)}
              placeholder="Firma ayarını ezer"
            />
            <small className="text-[10px] opacity-60">Firma genel ayarını ezmek için doldurun.</small>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-semibold">Özel Maks. İskonto (%)</label>
            <InputText
              type="number"
              step="0.1"
              value={formData.integrationDetail?.customMaxDiscountRate?.toString() || ""}
              onChange={(e) => handleIntegrationChange('customMaxDiscountRate', e.target.value ? parseFloat(e.target.value) : null)}
              placeholder="Firma ayarını ezer"
            />
            <small className="text-[10px] opacity-60">Siparişte kullanabileceği max iskonto oranını ezer.</small>
          </div>
        </div>
      </div>
    </div>
  );

  const tabs = useMemo(() => {
    const baseTabs = [
      {
        id: 'basic',
        header: <><UserIcon className="w-4 h-4 mr-2" /> Temel Bilgiler</>,
        content: (
          <div className="space-y-4 p-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2"><label className="font-semibold text-sm">Ad Soyad *</label><InputText value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} /></div>
              <div className="flex flex-col gap-2"><label className="font-semibold text-sm">E-posta *</label><InputText value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} /></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col gap-2">
                <label className="font-semibold text-sm">Şifre {user ? "(Opsiyonel)" : "*"}</label>
                <Password value={formData.password} onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))} toggleMask feedback={false} className="w-full" inputClassName="w-full" />
              </div>
              <div className="flex flex-col gap-2">
                <label className="font-semibold text-sm">PIN Kodu (Opsiyonel)</label>
                <div className="flex gap-2">
                  <div className="relative w-full">
                    <InputText
                      value={formData.pinCode || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        latestPinRef.current = val;
                        setFormData(p => ({ ...p, pinCode: val }));
                        if (val.length === 4) {
                          checkPin(val);
                        } else {
                          setIsPinValid(null);
                        }
                      }}
                      onBlur={handlePinBlur}
                      placeholder="Örn: 1000"
                      className="w-full pr-10"
                      maxLength={4}
                      keyfilter="pint"
                    />
                    {isPinValid === true && <CheckCircle size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-green-600 z-50 pointer-events-none" />}
                  </div>
                  <button
                    type="button"
                    onClick={handleGeneratePin}
                    className="bg-primary flex items-center justify-center min-w-[70px] text-white px-3 py-2 rounded-lg font-bold text-sm hover:bg-primary-hover transition-colors whitespace-nowrap"
                    title="Otomatik PIN Üret"
                  >
                    Üret
                  </button>
                </div>
              </div>
            </div>
            <div className="space-y-4  ">
              <div className="flex flex-col gap-2">
                <label className="font-semibold text-sm">Rol *</label>
                <Dropdown
                  value={formData.roleId}
                  options={roleOptions}
                  optionLabel="displayName"
                  optionValue="id"
                  onChange={(e) => {
                    const selectedRole = roleOptions.find(r => r.id === e.value);
                    setFormData(p => ({ ...p, roleId: e.value, role: selectedRole }));
                  }}
                />
              </div>

              {/* Onay Durumu */}
              <div className="flex flex-col gap-2 card p-3 rounded-xl border-l-4 border-l-primary shadow-sm border mt-4">
                <div className="flex justify-between items-center">
                  <label className="font-bold text-sm text-primary flex items-center gap-2">
                    <UserCheck size={16} /> Kullanıcı Onaylı Mı?
                  </label>
                  <InputSwitch checked={!!formData.isActive} onChange={(e) => setFormData(p => ({ ...p, isActive: e.value }))} />
                </div>
                <small className="text-text opacity-60 italic">
                  * Onaylı olmayan kullanıcılar sisteme giriş yapamaz veya işlem yapamaz.
                </small>
              </div>

              {/* ROL MÜŞTERİ İSE CARİ SEÇİMİNİ GÖSTER */}
              {(formData.role?.name === 'customer' || formData.role?.name === 'client') && (
                <div className="flex flex-col gap-2 card2 p-3 rounded-xl border border-[var(--border)]">
                  <CustomSelectInput
                    label={<><CircleUser size={14} className="text-primary" /> Bağlı Olduğu Cari (Account)</>}
                    value={formData.account || ""}
                    placeholder="Cari Seçmek İçin Tıklayın..."
                    icon={Search}
                    onClick={openAccountSelect}
                    onClear={formData.accountId ? () => setFormData(p => ({ ...p, accountId: null, account: null })) : undefined}
                  />
                  <small className="text-gray-500 italic mt-1">
                    * Bu kullanıcı sipariş verdiğinde bu cari üzerinden işlem yapılacaktır.
                  </small>
                </div>
              )}
            </div> </div>
        )
      },

    ];

    const roleName = formData.role?.name;
    if (roleName?.includes('warehouse')) {
      baseTabs.push({ id: 'staff-detail', header: <><Activity className="w-4 h-4 mr-2" /> Depo</>, content: renderStaffTab() });
    }
    if (roleName?.includes('sales') || roleName === 'firm_admin') {
      baseTabs.push({ id: 'integration-detail', header: <><Plus className="w-4 h-4 mr-2" /> Saha Ayarları</>, content: renderIntegrationTab() });
    }

    if (user && user.id) {
      baseTabs.push({
        id: 'print-settings',
        header: <><PrinterIcon className="w-4 h-4 mr-2" /> Yazdırma Ayarları</>,
        content: <div className="p-2"><AccountPrintSettings userId={user.id} /></div>
      });
    }

    return baseTabs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, roleOptions, user]);

  return (
    <Dialog
      header={user ? `${formData.name} - Düzenle` : "Yeni Kullanıcı Oluştur"}
      visible={isOpen} onHide={onClose} modal maximizable maximized className="w-full md:w-8/12 lg:w-7/12"
      footer={
        <div className="flex justify-end gap-3">
          <IconTextButton onClick={onClose} icon={X} className="btn-cancel">İptal</IconTextButton>
          <IconTextButton onClick={handleSubmit} icon={user ? Save : Plus} className="btn-new">{user ? "Güncelle" : "Kaydet"}</IconTextButton>
        </div>
      }
    >
      <ETabs tabs={tabs} initialActiveIndex={activeIndex} onTabChange={setActiveIndex} />
      {accountSelectDialog.modal}
      {employeeSelect.modal}
      {cashSelect.modal}
      {bankSelect.modal}
      {warehouseSelect.modal}
    </Dialog>
  );
};